import { DashboardModal } from "@dashboard/components/Modal";
import { Box, Button, Select, Text } from "@saleor/macaw-ui-next";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

interface FrequencyConfig {
  hour: number;
  minute: number;
  timezone: string;
  frequency: "hourly" | "daily" | "weekly" | "monthly";
  day_of_week?: number;
  day_of_month?: number;
}

interface FrequencyScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (config: FrequencyConfig) => void;
  initialConfig?: FrequencyConfig;
}

const FREQUENCY_OPTIONS = [
  { label: "Hourly", value: "hourly" },
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

const DAY_OF_WEEK_OPTIONS = [
  { label: "Monday", value: "1" },
  { label: "Tuesday", value: "2" },
  { label: "Wednesday", value: "3" },
  { label: "Thursday", value: "4" },
  { label: "Friday", value: "5" },
  { label: "Saturday", value: "6" },
  { label: "Sunday", value: "0" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  label: `${i.toString().padStart(2, '0')}:00`,
  value: i.toString(),
}));

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
  label: i.toString().padStart(2, '0'),
  value: i.toString(),
}));

const HOURLY_STEP_OPTIONS = [
  { label: "Every hour", value: "1" },
  { label: "Every 2 hours", value: "2" },
  { label: "Every 3 hours", value: "3" },
  { label: "Every 4 hours", value: "4" },
  { label: "Every 6 hours", value: "6" },
  { label: "Every 8 hours", value: "8" },
  { label: "Every 12 hours", value: "12" },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  label: (i + 1).toString(),
  value: (i + 1).toString(),
}));

const COMMON_TIMEZONES = [
  { label: "Asia/Karachi (PKT)", value: "Asia/Karachi" },
  { label: "UTC", value: "UTC" },
  { label: "America/New_York (EST/EDT)", value: "America/New_York" },
  { label: "America/Los_Angeles (PST/PDT)", value: "America/Los_Angeles" },
  { label: "Europe/London (GMT/BST)", value: "Europe/London" },
  { label: "Asia/Tokyo (JST)", value: "Asia/Tokyo" },
  { label: "Australia/Sydney (AEST/AEDT)", value: "Australia/Sydney" },
];

export const FrequencyScheduleModal: React.FC<FrequencyScheduleModalProps> = ({
  open,
  onClose,
  onUpdate,
  initialConfig,
}) => {
  const defaultConfig: FrequencyConfig = {
    frequency: "monthly",
    hour: 12,
    minute: 0,
    timezone: "Asia/Karachi",
    day_of_month: 1,
    day_of_week: 1,
  };

  const configToUse = initialConfig || defaultConfig;

  const { control, watch, setValue } = useForm<FrequencyConfig>({
    defaultValues: configToUse,
  });

  const frequencyType = watch("frequency");

  useEffect(() => {
    if (open) {
      setValue("frequency", configToUse.frequency);
      setValue("hour", configToUse.hour);
      setValue("minute", configToUse.minute);
      setValue("timezone", configToUse.timezone);
      setValue("day_of_month", configToUse.day_of_month || 1);
      setValue("day_of_week", configToUse.day_of_week || 1);
    }
  }, [open, configToUse, setValue]);

  const handleDone = () => {
    // Get current form values
    const formValues = watch();
    
    // Convert string values back to numbers for hour, minute, day_of_month
    const processedData = {
      ...formValues,
      hour: typeof formValues.hour === 'string' ? parseInt(formValues.hour, 10) : formValues.hour,
      minute: typeof formValues.minute === 'string' ? parseInt(formValues.minute, 10) : formValues.minute,
      day_of_month: typeof formValues.day_of_month === 'string' ? parseInt(formValues.day_of_month, 10) : formValues.day_of_month,
      day_of_week: typeof formValues.day_of_week === 'string' ? parseInt(formValues.day_of_week, 10) : formValues.day_of_week,
    };
    
    onUpdate(processedData);
    onClose();
  };


  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="sm">
        <DashboardModal.Header>
          <DashboardModal.Title>Edit schedule</DashboardModal.Title>
        </DashboardModal.Header>
        
        <Box padding={6} display="flex" flexDirection="column" gap={4}>
            {/* Frequency Type */}
            <Box>
              <Text size={3} fontWeight="medium" marginBottom={2}>
                Frequency
              </Text>
              <Controller
                control={control}
                name="frequency"
                render={({ field }) => (
                  <Select 
                    {...field} 
                    options={FREQUENCY_OPTIONS}
                  />
                )}
              />
            </Box>

            {/* Hourly Fields */}
            {frequencyType === "hourly" && (
              <>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Hours Step
                  </Text>
                  <Controller
                    control={control}
                    name="hour"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={HOURLY_STEP_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Minute
                  </Text>
                  <Controller
                    control={control}
                    name="minute"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={MINUTE_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Timezone
                  </Text>
                  <Controller
                    control={control}
                    name="timezone"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        options={COMMON_TIMEZONES}
                      />
                    )}
                  />
                </Box>
              </>
            )}

            {/* Daily Fields */}
            {frequencyType === "daily" && (
              <>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Hour
                  </Text>
                  <Controller
                    control={control}
                    name="hour"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={HOUR_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Minute
                  </Text>
                  <Controller
                    control={control}
                    name="minute"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={MINUTE_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Timezone
                  </Text>
                  <Controller
                    control={control}
                    name="timezone"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        options={COMMON_TIMEZONES}
                      />
                    )}
                  />
                </Box>
              </>
            )}

            {/* Weekly Fields */}
            {frequencyType === "weekly" && (
              <>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Day of Week
                  </Text>
                  <Controller
                    control={control}
                    name="day_of_week"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={DAY_OF_WEEK_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Hour
                  </Text>
                  <Controller
                    control={control}
                    name="hour"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={HOUR_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Minute
                  </Text>
                  <Controller
                    control={control}
                    name="minute"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={MINUTE_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Timezone
                  </Text>
                  <Controller
                    control={control}
                    name="timezone"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        options={COMMON_TIMEZONES}
                      />
                    )}
                  />
                </Box>
              </>
            )}

            {/* Monthly Fields */}
            {frequencyType === "monthly" && (
              <>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Date
                  </Text>
                  <Controller
                    control={control}
                    name="day_of_month"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={DAY_OF_MONTH_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Hour
                  </Text>
                  <Controller
                    control={control}
                    name="hour"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={HOUR_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Minute
                  </Text>
                  <Controller
                    control={control}
                    name="minute"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        value={field.value?.toString()}
                        options={MINUTE_OPTIONS}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Text size={3} fontWeight="medium" marginBottom={2}>
                    Timezone
                  </Text>
                  <Controller
                    control={control}
                    name="timezone"
                    render={({ field }) => (
                      <Select 
                        {...field} 
                        options={COMMON_TIMEZONES}
                      />
                    )}
                  />
                </Box>
              </>
            )}
          </Box>

          <DashboardModal.Actions>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDone}>
              Done
            </Button>
          </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};